import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import f1Service from '../../../components/dashboards/services/f1Service';

/**
 * F1 Race Weekend Timeline Component
 * Shows the complete weekend schedule: Practice → Qualifying → Sprint → Race
 * Displays fastest laps, session status, and progressive improvements
 */
const F1RaceWeekendTimeline = ({ season = 2025 }) => {
  const [weekends, setWeekends] = useState([]);
  const [selectedWeekend, setSelectedWeekend] = useState(null);
  const [weekendSessions, setWeekendSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
 
  // Fetch all race weekends
  useEffect(() => {
    const fetchWeekends = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get all races for the season
        const racesResponse = await f1Service.getCurrentSeasonRaces();
        if (!racesResponse?.response) {
          throw new Error('No race data available');
        }

        // Filter for main races only (not practice/quali)
        const mainRaces = racesResponse.response.filter(r => r.type === 'Race');
        setWeekends(mainRaces);

        // Auto-select the most recent or next race
        if (mainRaces.length > 0) {
          const now = new Date();
          const upcoming = mainRaces.find(r => new Date(r.date) >= now);
          const selected = upcoming || mainRaces[mainRaces.length - 1];
          setSelectedWeekend(selected.id);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching weekends:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchWeekends();
  }, [season]);

  // Fetch sessions for selected weekend
  useEffect(() => {
    if (!selectedWeekend) return;

    const fetchWeekendSessions = async () => {
      try {
        const selectedRace = weekends.find(w => w.id === selectedWeekend);
        if (!selectedRace) return;

        const competitionId = selectedRace.competition?.id;
        if (!competitionId) return;

        // Get all sessions for this competition
        const allRacesResponse = await f1Service.getCurrentSeasonRaces();
        const allSessions = allRacesResponse?.response || [];

        // Filter sessions for this competition
        const sessions = allSessions.filter(s =>
          s.competition?.id === competitionId
        );

        // Sort sessions chronologically
        const sortedSessions = sessions.sort((a, b) =>
          new Date(a.date) - new Date(b.date)
        );

        // Fetch fastest lap/time for each completed session
        const sessionsWithData = await Promise.all(
          sortedSessions.map(async (session) => {
            let fastestLap = null;
            if (session.status === 'Completed') {
              try {
                // For practice/qualifying sessions, use starting grid endpoint
                // For race/sprint, use fastest laps endpoint
                const isPracticeOrQuali = session.type?.includes('Practice') || session.type?.includes('Qualifying');

                if (isPracticeOrQuali) {
                  // Use starting grid which gives us practice/quali times
                  const gridResponse = await f1Service.getStartingGrid(session.id);
                  if (gridResponse?.response?.[0]) {
                    fastestLap = gridResponse.response[0]; // P1 has fastest time
                  }
                } else {
                  // For race/sprint, use fastest laps
                  const lapsResponse = await f1Service.getFastestLaps(session.id);
                  if (lapsResponse?.response?.[0]) {
                    fastestLap = lapsResponse.response[0];
                  }
                }
              } catch (err) {
                console.log(`No data for session ${session.id}:`, err.message);
              }
            }
            return { ...session, fastestLap };
          })
        );

        setWeekendSessions(sessionsWithData);
      } catch (err) {
        console.error('Error fetching weekend sessions:', err);
      }
    };

    fetchWeekendSessions();
  }, [selectedWeekend, weekends]);

  // Get session type icon/label
  const getSessionInfo = (type) => {
    const typeMap = {
      '1st Practice': { label: 'FP1', order: 1, color: '#6B7280' },
      '2nd Practice': { label: 'FP2', order: 2, color: '#6B7280' },
      '3rd Practice': { label: 'FP3', order: 3, color: '#6B7280' },
      '1st Qualifying': { label: 'Q1', order: 4, color: '#F59E0B' },
      '2nd Qualifying': { label: 'Q2', order: 5, color: '#F59E0B' },
      '3rd Qualifying': { label: 'Q3', order: 6, color: '#F59E0B' },
      'Sprint': { label: 'Sprint', order: 7, color: '#8B5CF6' },
      'Sprint Shootout': { label: 'Sprint Quali', order: 7, color: '#8B5CF6' },
      'Race': { label: 'Race', order: 8, color: '#E10600' }
    };
    return typeMap[type] || { label: type, order: 9, color: '#6B7280' };
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return '#10B981';
      case 'Live': return '#E10600';
      case 'Scheduled': return '#F59E0B';
      default: return '#6B7280';
    }
  };

  // Format date/time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format lap time
  const formatLapTime = (time) => {
    if (!time) return 'N/A';
    return time;
  };

  // Get team color
  const getTeamColor = (teamName) => {
    const colors = f1Service.getTeamColors(teamName);
    return colors.primary;
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <div className="h-4 w-48 bg-white/10 rounded animate-pulse" />
        </div>
        <div className="p-4">
          <div className="flex gap-4 overflow-x-auto">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex-shrink-0 w-64 h-48 bg-white/10 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white/[0.02] border border-red-500/20 rounded-sm p-4">
        <p className="text-xs text-red-400 font-mono">Error: {error}</p>
      </div>
    );
  }

  // Empty state
  if (weekends.length === 0) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm p-8 text-center">
        <p className="text-xs text-gray-600 font-mono uppercase tracking-wider">
          No Race Weekend Data Available
        </p>
      </div>
    );
  }

  const selectedRace = weekends.find(w => w.id === selectedWeekend);

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">
            Race Weekend Timeline
          </h3>
          <span className="text-[10px] text-gray-600 font-mono uppercase tracking-wider">
            {season}
          </span>
        </div>

        {/* Weekend Selector */}
        {weekends.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <span className="text-[10px] font-mono text-gray-600 uppercase tracking-wider whitespace-nowrap">
              Weekend:
            </span>
            <div className="flex gap-2">
              {weekends.map((weekend) => (
                <button
                  key={weekend.id}
                  onClick={() => setSelectedWeekend(weekend.id)}
                  className={`px-3 py-1 text-[10px] font-mono uppercase tracking-wider transition-all duration-150 border whitespace-nowrap ${
                    selectedWeekend === weekend.id
                      ? 'bg-white/10 text-white border-white/20'
                      : 'bg-transparent text-gray-600 border-white/5 hover:border-white/10'
                  }`}
                >
                  {weekend.competition?.name?.split(' ')[0] || `R${weekend.competition?.location?.round || weekends.indexOf(weekend) + 1}`}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Selected Weekend Info */}
      {selectedRace && (
        <div className="p-4 border-b border-white/5 bg-white/[0.01]">
          <div className="text-sm font-bold text-white mb-1">
            {selectedRace.competition?.name || 'Grand Prix'}
          </div>
          <div className="text-xs text-gray-400 font-mono">
            {selectedRace.circuit?.name || 'Circuit'} • {selectedRace.competition?.location?.country || 'Country'}
          </div>
        </div>
      )}

      {/* Weekend Timeline */}
      <div className="p-4 overflow-x-auto">
        <div className="flex gap-4 pb-2">
          {weekendSessions.length > 0 ? (
            weekendSessions.map((session, index) => {
              const sessionInfo = getSessionInfo(session.type);
              const statusColor = getStatusColor(session.status);
              const teamColor = session.fastestLap?.team?.name
                ? getTeamColor(session.fastestLap.team.name)
                : '#6B7280';

              return (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.3 }}
                  className="flex-shrink-0 w-72"
                >
                  {/* Session Card */}
                  <div
                    className="border rounded-sm h-full"
                    style={{
                      backgroundColor: `${sessionInfo.color}08`,
                      borderColor: `${sessionInfo.color}40`
                    }}
                  >
                    {/* Session Header */}
                    <div
                      className="p-3 border-b"
                      style={{ borderColor: `${sessionInfo.color}20` }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div
                          className="text-xs font-bold uppercase tracking-wider"
                          style={{ color: sessionInfo.color }}
                        >
                          {sessionInfo.label}
                        </div>
                        <div
                          className="text-[9px] font-bold font-mono uppercase px-2 py-0.5 rounded"
                          style={{
                            backgroundColor: `${statusColor}20`,
                            color: statusColor
                          }}
                        >
                          {session.status}
                        </div>
                      </div>
                      <div className="text-[10px] text-gray-500 font-mono">
                        {formatDateTime(session.date)}
                      </div>
                    </div>

                    {/* Session Details */}
                    <div className="p-3 space-y-3">
                      {/* Fastest Lap */}
                      {session.fastestLap ? (
                        <>
                          <div className="text-[10px] text-gray-600 uppercase tracking-wider">
                            Fastest Lap
                          </div>

                          <div className="flex items-center gap-2">
                            <div
                              className="w-1 h-12 rounded-full"
                              style={{ backgroundColor: teamColor }}
                            />
                            <div className="flex-1">
                              <div className="text-xs font-bold text-white">
                                {session.fastestLap.driver?.name || 'Unknown'}
                              </div>
                              <div className="text-[10px] text-gray-500 font-mono">
                                {f1Service.getTeamCode(session.fastestLap.team?.name)}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-2 border-t border-white/5">
                            <span className="text-[10px] text-gray-600 uppercase tracking-wider">Time</span>
                            <span className="text-sm font-bold font-mono text-white tabular-nums">
                              {formatLapTime(session.fastestLap.time)}
                            </span>
                          </div>

                          {session.fastestLap.avg_speed && (
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] text-gray-600 uppercase tracking-wider">Speed</span>
                              <span className="text-xs font-mono text-gray-400 tabular-nums">
                                {session.fastestLap.avg_speed}
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="py-6 text-center">
                          <p className="text-[10px] text-gray-600 font-mono uppercase tracking-wider">
                            {session.status === 'Completed' ? 'No Data Available' : 'Awaiting Session'}
                          </p>
                        </div>
                      )}

                      {/* Laps */}
                      {session.laps && (
                        <div className="flex items-center justify-between pt-2 border-t border-white/5">
                          <span className="text-[10px] text-gray-600 uppercase tracking-wider">Laps</span>
                          <span className="text-xs font-mono text-gray-400 tabular-nums">
                            {session.laps.current || 0} / {session.laps.total || '?'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Session Color Bar */}
                    <div
                      className="h-1"
                      style={{ backgroundColor: sessionInfo.color }}
                    />
                  </div>
                </motion.div>
              );
            })
          ) : (
            <div className="w-full text-center py-12">
              <p className="text-xs text-gray-600 font-mono uppercase tracking-wider">
                No Session Data Available
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-white/5 bg-white/[0.01]">
        <div className="flex items-center justify-between text-[10px] text-gray-600 font-mono uppercase tracking-wider">
          <span>Sessions This Weekend</span>
          <span className="text-white font-bold">{weekendSessions.length}</span>
        </div>
      </div>
    </div>
  );
};

export default F1RaceWeekendTimeline;
